// <s-background-video>: ambient, muted hero video that loops forever.
// Loading: the poster paints first; the video's <source data-src> URLs are only
// activated after window load (or idle), so the file never competes with the page.
// Data-saver visitors keep the poster. Pauses while off-screen to save battery and
// resumes automatically when it comes back into view.
class SBackgroundVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.visible = true;
    this.loaded = false;

    this.video.muted = true;
    this.video.loop = true;

    if (navigator.connection?.saveData) return;

    this.video.addEventListener('playing', () => this.video.setAttribute('data-ready', ''), { signal, once: true });
    // Safety net: restart if the browser ever stops at the end instead of looping.
    this.video.addEventListener('ended', () => {
      this.video.currentTime = 0;
      this.play();
    }, { signal });
    this.video.addEventListener('canplay', () => this.play(), { signal });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.play();
    }, { signal });

    // If autoplay was blocked (e.g. low-power mode), start on the first interaction.
    const kick = () => this.play();
    for (const type of ['pointerdown', 'touchstart', 'keydown', 'scroll']) {
      window.addEventListener(type, kick, { signal, once: true, passive: true });
    }

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.visible = entry.isIntersecting;
        if (entry.isIntersecting) {
          this.play();
        } else {
          this.video.pause();
        }
      }
    });
    this.observer.observe(this);

    const start = () => {
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
      idle(() => this.load(), { timeout: 1500 });
    };
    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { signal, once: true });
    }
  }

  load() {
    if (this.loaded || !this.video) return;
    this.loaded = true;
    for (const source of this.video.querySelectorAll('source[data-src]')) {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    }
    this.video.preload = 'auto';
    this.video.load();
    this.play();
  }

  play() {
    if (!this.loaded || !this.visible || !this.video) return;
    if (!this.video.paused) return;
    this.video.play().catch(() => {});
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }
}

if (!customElements.get('s-background-video')) customElements.define('s-background-video', SBackgroundVideo);
